(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.RwadyOrderConfirmation = api;
})(typeof globalThis === 'object' ? globalThis : this, function () {
  const validOrder = (result, requestId) => result?.ok === true && result.status === 'submitted'
    && result.request_id === requestId && /^gid:\/\/shopify\/Order\/\d+$/.test(result.order?.id || '')
    && typeof result.order.name === 'string' && result.order.name.length > 0
    && result.order.currency === 'IQD' && Number.isFinite(result.order.total) && result.order.total >= 0;
  const validPrepared = (result, requestId) => result?.ok === true && result.status === 'prepared'
    && result.request_id === requestId && result.confirmation?.currency === 'IQD'
    && Array.isArray(result.confirmation?.items) && result.confirmation.items.length > 0
    && Number.isFinite(result.confirmation?.total) && result.confirmation.total >= 0
    && Number.isFinite(result.confirmation?.shipping) && result.confirmation.shipping >= 0;
  const failure = (code) => Object.assign(new Error(code), { code });
  const canonical = (payload) => JSON.stringify({
    phone: payload.customer.phone, address: payload.customer.address,
    governorate: payload.governorate, city: payload.city, country: payload.country_code,
    payment: payload.payment_method, detail: payload.payment_detail,
    products_total: payload.expected_products_total,
    shipping: payload.shipping,
    order_total: payload.expected_order_total,
    discounts: [...(payload.discount_codes || [])].sort(),
    items: (payload.items || []).map((item) => [String(item.shopify_variant_id), item.quantity, item.selling_plan_id || '', item.properties || {}])
      .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)))
  });
  function create({ baseUrl, storage, cryptoImpl = globalThis.crypto, fetchImpl = globalThis.fetch, locks = globalThis.navigator?.locks }) {
    const key = 'rwady-native-checkout-v1';
    let record;
    const read = () => {
      try { record = JSON.parse(storage.getItem(key) || 'null'); }
      catch { throw failure('CHECKOUT_STORAGE_UNAVAILABLE'); }
      if (record && (!record.request_id || !record.fingerprint)) record = null;
      return record;
    };
    const save = () => {
      try { storage.setItem(key, JSON.stringify(record)); }
      catch { throw failure('CHECKOUT_STORAGE_UNAVAILABLE'); }
    };
    const fingerprint = async (payload) => {
      const bytes = await cryptoImpl.subtle.digest('SHA-256', new TextEncoder().encode(canonical(payload)));
      return Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, '0')).join('');
    };
    const bind = async (payload) => {
      const hash = await fingerprint(payload);
      read();
      if (record?.fingerprint !== hash) {
        if (record?.phase === 'submitting' || record?.phase === 'preparing') throw failure('REQUEST_IN_PROGRESS');
        record = { request_id: cryptoImpl.randomUUID(), fingerprint: hash, phase: 'ready', review: null, accepted_version: null, payment_method: payload.payment_method, payment_detail: payload.payment_detail || '' };
        save();
      }
      return record;
    };
    const post = async (action, payload, authorization, requestId, snapshot) => {
      try {
        const response = await fetchImpl(`${baseUrl.replace(/\/$/, '')}/sales/checkout-action`, {
          method: 'POST', credentials: 'omit', cache: 'no-store',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ action, request_id: requestId, verification_token: authorization.verification_token,
            payload: { ...payload, cart_fingerprint: snapshot.fingerprint,
              review_id: snapshot.review?.id, accepted_review_version: snapshot.accepted_version ?? undefined } })
        });
        return { response, result: await response.json() };
      } catch { throw failure('CHECKOUT_UNAVAILABLE'); }
    };
    const retryReady = (result, requestId) => result?.ok === true
      && result.submission_recovery?.request_id === requestId
      && result.submission_recovery.status === 'retry_ready';
    const run = async (action, payload, authorization) => {
      try { await bind(payload); }
      catch (error) {
        if (error.code !== 'REQUEST_IN_PROGRESS') throw error;
        const previous = read();
        if (previous?.phase !== 'submitting') throw error;
        const { response, result } = await post('review_status', payload, authorization, previous.request_id, previous);
        if (!response.ok || !retryReady(result, previous.request_id)) throw error;
        const hash = await fingerprint(payload);
        read();
        if (record?.request_id !== previous.request_id || record.phase !== 'submitting') throw error;
        record.fingerprint = hash;
        record.phase = 'ready';
        record.review = result.review ?? null;
        record.accepted_version = null;
        save();
      }
      record.payment_method = payload.payment_method;
      record.payment_detail = payload.payment_detail || '';
      if (record.phase === 'submitted') return record.result;
      const requestId = record.request_id;
      if (action === 'prepare') {
        record.phase = 'preparing';
        save();
      }
      if (action === 'submit') {
        if (record.review?.status === 'pending') throw failure('REVIEW_PENDING');
        if (record.review?.status === 'stale') throw failure('REVIEW_STALE');
        if (record.review?.status === 'approved') record.accepted_version = record.review.version;
        else record.accepted_version = null;
        record.phase = 'submitting';
        save();
      }
      const { response, result } = await post(action, payload, authorization, requestId, record);
      if (!response.ok || result.ok !== true) {
        const code = response.status === 404 ? 'CHECKOUT_NOT_ENABLED'
          : /^[A-Z0-9_]+$/.test(result?.code || '') ? result.code : 'CHECKOUT_UNAVAILABLE';
        if (record.phase === 'preparing') record.phase = 'ready';
        if (['INVALID_AUTHORIZATION', 'INVALID_PAYLOAD', 'CHECKOUT_NOT_ENABLED', 'PRICE_CHANGED', 'SHIPPING_CHANGED', 'OUT_OF_STOCK', 'DISCOUNT_INVALID', 'REVIEW_REQUIRED', 'REVIEW_PENDING', 'REVIEW_REJECTED', 'REVIEW_NOT_ACCEPTED', 'REVIEW_STALE', 'UNSUPPORTED_PAYMENT_METHOD', 'UNSUPPORTED_SELLING_PLAN', 'SHOPIFY_REJECTED', 'PRICING_UNAVAILABLE', 'SHIPPING_UNAVAILABLE', 'CURRENCY_MISMATCH', 'REQUEST_CONFLICT'].includes(code)) {
          record.phase = 'ready';
          if (code.startsWith('REVIEW_')) record.accepted_version = null;
          save();
        }
        throw failure(code);
      }
      if (action === 'submit') {
        if (!validOrder(result, requestId)) throw failure('ORDER_NOT_CONFIRMED');
        record.phase = 'submitted';
        record.payment_method = payload.payment_method;
        record.result = result;
        save();
      } else if (action === 'prepare') {
        if (validOrder(result, requestId)) {
          record.phase = 'submitted';
          record.result = result;
        } else {
          if (!validPrepared(result, requestId)) {
            record.phase = 'ready';
            save();
            throw failure('ORDER_CONFIRMATION_UNAVAILABLE');
          }
          record.phase = 'prepared';
          record.prepared = result;
        }
        save();
      } else {
        if (retryReady(result, requestId)) record.phase = 'ready';
        if (record.review?.version !== result.review?.version || record.review?.id !== result.review?.id) record.accepted_version = null;
        record.review = result.review;
        save();
      }
      return result;
    };
    return {
      peek: read,
      bind,
      request: (action, payload, authorization) => locks
        ? locks.request(key, () => run(action, payload, authorization)) : run(action, payload, authorization)
    };
  }
  return { create, validOrder, validPrepared, canonical };
});

